import { useEffect, useState } from "react";
import { Megaphone, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

export default function AdminAnnouncements() {
  const [form, setForm] = useState({ title: "", message: "", target: "all", plan: "starter", country: "" });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => adminApi.get("/admin/announcements").then((r) => setHistory(r.data.announcements || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title.trim() || !form.message.trim()) return toast.error("Add a title and message");
    setSending(true);
    try {
      const r = await adminApi.post("/admin/announcements", form);
      toast.success(`Announcement sent to ${r.data.recipients} customer(s)`);
      setForm({ ...form, title: "", message: "" }); load();
    } catch { toast.error("Could not send announcement"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-announcements-view">
      <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Megaphone className="h-6 w-6 text-blue-600" /> Platform Announcements</h1>
      <p className="text-slate-500 mt-1">Broadcast to all customers, a plan, a country or an individual. Delivery activates fully once an email provider is connected.</p>

      <div className="mt-5 grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <div><Label className="text-xs">Title</Label><Input data-testid="ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Message</Label><Textarea data-testid="ann-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1" /></div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs">Audience</Label>
              <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                <SelectTrigger data-testid="ann-target" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  <SelectItem value="plan">By plan</SelectItem>
                  <SelectItem value="country">By country</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.target === "plan" && (
              <div className="flex-1"><Label className="text-xs">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["starter", "professional", "business"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.target === "country" && (
              <div className="flex-1"><Label className="text-xs">Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" placeholder="e.g. UK" /></div>
            )}
          </div>
          <Button data-testid="ann-send-btn" onClick={send} disabled={sending} className="bg-blue-600 hover:bg-blue-700 gap-2 w-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send announcement
          </Button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Recent announcements</h2>
          <div className="space-y-3 max-h-[420px] overflow-y-auto">
            {history.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No announcements sent yet.</p>}
            {history.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-100 p-3">
                <div className="flex items-center justify-between"><p className="font-medium text-slate-800 text-sm">{a.title}</p><span className="text-xs text-slate-400">{a.recipients} sent</span></div>
                <p className="text-sm text-slate-500 mt-1">{a.message}</p>
                <p className="text-[11px] text-slate-400 mt-1 capitalize">{a.target} · {new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
